/**
 * Partial payments: several invoices against one roster line (spec D9/D10,
 * subsystem 2).
 *
 * Subsystem 1 held one line to one invoice, and said so — D10 follows from D9,
 * which deferred splits to this subsystem. The sheet's "Instruction on
 * Payment: 100%" is the evidence they exist: a creator invoices half on
 * signing and half on delivery, and an editor bills per batch.
 *
 * So a line now carries a list of invoices, and the questions change from
 * "has it been invoiced" to "how much of the agreed price has been, and how
 * much of that has been paid". A duplicate stops being "a second invoice" and
 * becomes "the same amount submitted twice while one is still pending".
 *
 * Pure. Nothing here blocks: an over-invoiced line is flagged, never refused
 * (D6).
 */

import { LINE_STATES, lineState } from './rosterLine';

/** The state the split view adds to the line's own machine. */
export const SPLIT_STATES = {
  PART_SETTLED: 'part_settled',
};

export const SPLIT_STATE_LABELS = {
  [SPLIT_STATES.PART_SETTLED]: 'Part settled',
};

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

function number(value) {
  if (isBlank(value)) return null;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

const amount = (value) => number(value) ?? 0;

/** A rejected invoice was never a commitment, so it counts for nothing. */
const committed = (invoice) => invoice && invoice.status !== 'rejected';

/**
 * Whether an invoice record carries what this module reads off it. Zero is a
 * real amount and an empty string is not, so this tests for the field rather
 * than for truthiness.
 */
const usable = (invoice) =>
  Boolean(invoice) && !isBlank(invoice.amount) && !isBlank(invoice.status);

/**
 * The agreed payout, or 0 when none has been agreed.
 *
 * The same figure has two names depending on which record you are holding: a
 * roster line calls it `influencer_final_price` (what `projectChecks` and
 * `linePayload` use), an invoice calls it `agreed_amount`. Reading only the
 * second meant every summary built from a roster line agreed nothing.
 */
const agreedOn = (line) => {
  const row = line || {};
  return amount(isBlank(row.influencer_final_price) ? row.agreed_amount : row.influencer_final_price);
};

/**
 * Which line an invoice belongs to, whichever key the API used.
 *
 * `shortlisted_influencer_id` is the one the live payload actually carries —
 * it is what `matchInvoice` scores on. The other two are the documented
 * shape for when the line endpoint starts naming its invoices directly.
 */
function belongsTo(invoice, line) {
  const keys = [line.shortlist_id, line.id].filter((key) => !isBlank(key));
  return keys.some(
    (key) =>
      invoice.shortlist_id === key ||
      invoice.line_id === key ||
      invoice.shortlisted_influencer_id === key,
  );
}

/** The invoices raised against a line, oldest first. */
export function splitsOf(line, invoices) {
  const row = line || {};

  // The shortlist row now names its own invoices, already oldest first. That
  // is the server's own answer to which invoices sit on this line, and it is
  // right before the flat invoice list has finished loading. An empty array
  // is not an answer, so fall through to matching when the row carries one.
  //
  // Trusted only when every entry can actually be read. This summary totals
  // on `amount` and tells a commitment from a rejection on `status`; a
  // nested serializer thinner than the flat one would make a rejected
  // invoice count as invoiced and a settled line never settle. The flat list
  // is the shape subsystem 1 shipped, so falling back to it loses nothing.
  if (Array.isArray(row.invoices) && row.invoices.length > 0 && row.invoices.every(usable)) {
    return row.invoices;
  }

  const list = Array.isArray(invoices) ? invoices : [];

  return list
    .filter((invoice) => invoice && belongsTo(invoice, row))
    .map((invoice, index) => ({ invoice, index }))
    .sort((a, b) => {
      const left = String(a.invoice.submitted_at ?? '');
      const right = String(b.invoice.submitted_at ?? '');
      // Stable: equal timestamps keep the order they arrived in.
      return left === right ? a.index - b.index : left.localeCompare(right);
    })
    .map(({ invoice }) => invoice);
}

/**
 * How much of the agreed price has been invoiced, and how much of that has
 * been paid.
 *
 * `percent` is null when nothing was agreed — comparing against zero is noise
 * rather than a finding, the same rule the project header follows.
 */
export function splitSummary(line, invoices) {
  const agreed = agreedOn(line);
  const splits = splitsOf(line, invoices).filter(committed);

  const invoiced = splits.reduce((total, invoice) => total + amount(invoice.amount), 0);
  const paid = splits
    .filter((invoice) => invoice.status === 'paid')
    .reduce((total, invoice) => total + amount(invoice.amount), 0);

  const over = agreed > 0 && invoiced > agreed;

  // What is left is measured against the price owed today, because that is
  // the live commitment. The invoices raised against a different figure are
  // reported separately rather than folded in: a creator who invoiced half of
  // 40,000 did not invoice a third, and silently restating it as a third is
  // the defect this closes. Needs a price to compare against, so a line with
  // nothing agreed reports nothing. Invoices raised before the payout was
  // frozen at submission carry 0, which means "not recorded", not "raised
  // against nothing" -- counting them flagged most older lines on prod.
  const repricedSplits =
    agreed > 0
      ? splits.filter((invoice) => {
          const atSubmission = number(invoice.agreed_amount_at_submission);
          return atSubmission !== null && atSubmission > 0 && atSubmission !== agreed;
        })
      : [];

  return {
    agreed,
    invoiced,
    paid,
    outstanding: invoiced - paid,
    remaining: agreed > 0 ? Math.max(0, agreed - invoiced) : 0,
    percent: agreed > 0 ? Math.min(100, Math.round((invoiced / agreed) * 100)) : null,
    over,
    overBy: over ? invoiced - agreed : 0,
    count: splits.length,
    repriced: repricedSplits.length > 0,
    repricedCount: repricedSplits.length,
  };
}

/**
 * The share of the agreed price one invoice is for — the sheet's "Instruction
 * on Payment". Can exceed a hundred, because over-invoicing happens.
 */
export function instructionPercent(value, agreed) {
  const price = amount(agreed);
  if (price <= 0) return null;
  return Math.round((amount(value) / price) * 100);
}

/** What to prefill the next split with, or null when there is nothing left. */
export function suggestSplit(line, invoices) {
  const summary = splitSummary(line, invoices);
  if (summary.agreed <= 0) return null;
  return summary.remaining > 0 ? summary.remaining : null;
}

/** `{field: message}`; empty means valid. */
export function validateSplit(form) {
  const values = form || {};
  const errors = {};

  const value = number(values.amount);
  if (isBlank(values.amount)) {
    errors.amount = 'Enter the amount this invoice is for.';
  } else if (value === null) {
    errors.amount = 'Enter an amount in numbers.';
  } else if (value <= 0) {
    errors.amount = 'Enter an amount above zero.';
  }

  if (!isBlank(values.percent)) {
    const percent = number(values.percent);
    if (percent === null || percent < 0 || percent > 100) {
      errors.percent = 'Enter a share between 0 and 100.';
    }
  }

  return errors;
}

/**
 * The split this one would duplicate, or null.
 *
 * Not simply "a second invoice on the line" — that is the feature. A duplicate
 * is the same amount submitted again while the first is still moving, which is
 * what a double submission looks like.
 */
export function duplicateSplit(form, { line, invoices } = {}) {
  const value = number((form || {}).amount);
  if (value === null) return null;

  return (
    splitsOf(line, invoices).find(
      (invoice) =>
        committed(invoice) &&
        invoice.status !== 'paid' &&
        amount(invoice.amount) === value,
    ) || null
  );
}

/**
 * The line's state once splits are in play.
 *
 * Defers to the single-invoice machine when a line has no splits, so nothing
 * about subsystem 1's behaviour changes until a line actually has more than
 * one invoice on it.
 */
export function lineStateWithSplits(line, invoices) {
  const splits = splitsOf(line, invoices).filter(committed);
  if (splits.length === 0) return lineState(line);

  const summary = splitSummary(line, invoices);
  const allPaid = splits.every((invoice) => invoice.status === 'paid');
  const fullyInvoiced = summary.agreed <= 0 || summary.invoiced >= summary.agreed;

  if (allPaid && fullyInvoiced) return LINE_STATES.SETTLED;
  if (summary.paid > 0) return SPLIT_STATES.PART_SETTLED;
  return LINE_STATES.INVOICED;
}

/** The request body for one split. */
export function splitPayload(form, line) {
  const values = form || {};
  const row = line || {};
  const value = amount(values.amount);

  const percent = isBlank(values.percent)
    ? instructionPercent(value, agreedOn(row))
    : number(values.percent);

  return {
    amount: value,
    shortlist_id: isBlank(row.shortlist_id) ? row.id : row.shortlist_id,
    ...(percent === null ? {} : { payment_instruction: `${percent}%` }),
  };
}
